"use client";

// ЕДИНОЕ подтверждение действия портала — замена window.confirm().
//
// ЗАЧЕМ. Аудит 08.08 нашёл 93 вызова window.confirm()/alert(). Это серое
// системное окно браузера поверх фирменного интерфейса: чужой шрифт, чужие
// кнопки, заголовок вида «На portal.young-unicorn.ru говорится», кнопки «ОК» и
// «Отмена» вместо осмысленных «Удалить»/«Отмена». Плюс confirm() блокирует
// поток — на медленной вкладке окно подвисает вместе со страницей.
//
// Как у тостов: намеренно НЕ контекст, а крошечная шина. Подтверждение зовут из
// обработчиков и из глубины деревьев, куда провайдер пришлось бы прокидывать
// через десяток компонентов:
//
//   if (!(await confirmDialog({ title: "Удалить сотрудника?", danger: true }))) return;
//
// <ConfirmHost/> монтируется один раз в оболочке портала (portal-frame).

import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Z } from "@/lib/z";

export type ConfirmOptions = {
  title: string;
  /** Пояснение под заголовком: что именно произойдёт и что будет необратимо. */
  text?: ReactNode;
  /** Подпись главной кнопки. По умолчанию — «Продолжить». */
  confirmText?: string;
  cancelText?: string;
  /** Действие разрушающее (удаление) — главная кнопка красная. */
  danger?: boolean;
  /** ВТОРОЕ действие рядом с главным — когда выбор не «да/нет», а «одно или другое»
   *  (импорт: заменить всё ИЛИ дополнить). Тогда «Отмена» отменяет ОБА варианта.
   *  Используйте через chooseDialog(), см. ниже. */
  altText?: string;
};

/** Что выбрал пользователь: главное действие, второе или ничего. */
export type ConfirmChoice = "confirm" | "alt" | null;

type Pending = ConfirmOptions & { id: number; resolve: (choice: ConfirmChoice) => void };

let current: Pending | null = null;
let seq = 0;
const listeners = new Set<(p: Pending | null) => void>();

function emit() {
  listeners.forEach((l) => l(current));
}

/** Спросить подтверждение. Возвращает true, если пользователь согласился. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return chooseDialog(opts).then((c) => c === "confirm");
}

/**
 * Диалог с ДВУМЯ действиями и отменой — когда выбор не «да/нет», а «одно или другое».
 *
 * Появился из импорта Google Sheets: там стоял window.confirm, где «ОК» означало
 * «заменить всё», а «Отмена» — «дополнить существующие». То есть Esc или крестик
 * молча ЗАПУСКАЛИ импорт в другом режиме. Здесь оба варианта — кнопки, а отмена
 * (Esc, клик по фону) не делает ничего.
 */
export function chooseDialog(opts: ConfirmOptions): Promise<ConfirmChoice> {
  // Если предыдущий вопрос ещё висит (например, два быстрых клика по «Удалить»),
  // закрываем его отказом — иначе первый промис никогда не разрешится.
  if (current) current.resolve(null);
  return new Promise<ConfirmChoice>((resolve) => {
    current = { ...opts, id: ++seq, resolve };
    emit();
  });
}

function settle(choice: ConfirmChoice) {
  const p = current;
  current = null;
  emit();
  p?.resolve(choice);
}

export function subscribeConfirm(l: (p: Pending | null) => void) {
  listeners.add(l);
  l(current);
  return () => {
    listeners.delete(l);
  };
}

export function ConfirmHost() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return subscribeConfirm(setPending);
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      // Esc — отказ, Enter — согласие: обычная клавиатурная логика диалога.
      if (e.key === "Escape") {
        e.stopPropagation();
        settle(null);
      } else if (e.key === "Enter") {
        e.stopPropagation();
        settle("confirm");
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pending]);

  if (!mounted || !pending) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: Z.CONFIRM }}
      onClick={() => settle(null)}
      role="alertdialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/40" aria-hidden />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[min(420px,calc(100vw-32px))] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] elev-modal"
      >
        <div className="px-5 pb-4 pt-5">
          <h2 className="widget-title text-[16px] leading-tight">{pending.title}</h2>
          {pending.text && (
            <p className="mt-2 text-[12px] leading-relaxed text-[var(--textMuted)]">{pending.text}</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-5 py-3">
          {/* Отказ от действия свипом не заливаем: фиолетовая заливка даёт кнопке
              вес главного действия, а главное здесь — соседняя. */}
          <Button noSweep onClick={() => settle(null)}>{pending.cancelText ?? "Отмена"}</Button>
          {pending.altText && (
            <Button onClick={() => settle("alt")}>{pending.altText}</Button>
          )}
          <Button
            variant={pending.danger ? "danger" : "primary"}
            onClick={() => settle("confirm")}
            autoFocus
          >
            {pending.confirmText ?? "Продолжить"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
